/**
 * What a `page_content` entry is allowed to carry into the database (SCRUM-1514).
 *
 * A page owns which variable it holds and what the editor typed into it. The
 * DECLARATION — `type`, `complextype`, `char_number`, and which of the two
 * columns the variable sits in (`variabletype`) — belongs to the template, and
 * every screen derives it from there on read (`resolveContentDeclarations`,
 * `mergePageContentWithTemplate`).
 *
 * Every create path used to build an entry by spreading the template variable
 * whole (`{ ...v, variabletype }`), so the declaration was copied onto the page
 * and stored with it. That copy is what the SCRUM-1511 migration deletes —
 * 39,850 fields over 1719 documents, measured 2026-09-08 — and building entries
 * through this function is what stops the next page putting it straight back.
 *
 * `_id` follows one rule, and the server's template sync
 * (`controllers/template.js`, the `added` entries) follows the same one: an
 * entry carries an `_id` only when the STORED entry already has one. The
 * template variable's `_id` is the template's and is never copied onto a page
 * (SCRUM-1548). This function used to fall back to `templateVariable._id`
 * when no id was passed, which made every create path and the main editor's
 * load path write the template's id onto the page — the fifth field of the
 * SCRUM-1472 copy. A stored `_id` is kept as-is: removing it from existing
 * documents is SCRUM-1520's audited apply, not a side effect of a page edit.
 *
 * `templateVariable` is the template's declaration of the variable; `value` is the
 * entry's initial value (pass nothing for "not filled in"); `id` is the stored
 * entry's own `_id`, if it has one.
 */
/** A page_content entry: the variable, its value when filled in, a stored `_id` when it has one. */
export type PageContentEntry = { variable: string | undefined; value?: unknown; _id?: unknown };

export function toPageContentEntry(
  templateVariable: { variable?: string },
  value?: unknown,
  id?: unknown
): PageContentEntry {
  const entry: PageContentEntry = { variable: templateVariable.variable };
  // A variable with no value at all keeps NO `value` key: that is the shape a
  // content variable has until an editor first fills it in, and the one
  // `pageContentGuard` documents as deliberately accepted. Writing
  // `value: undefined` here would normalise it away. Same for `_id`.
  if (value !== undefined) entry.value = value;
  if (id !== undefined) entry._id = id;
  return entry;
}
